import Alert from '@/components/alert/alert.component';
import { usePermissions, UserPermissions } from '@/modules/Permissions';
import { useChangeUser, User } from '@/modules/Users/Users.api';
import { useQueryClient } from '@tanstack/react-query';
import { forwardRef, Ref } from 'react';
import { toast } from 'react-toastify';

interface DeactivateUserAlertProps {
  user: User;
}

export const DeactivateUserAlert = forwardRef<HTMLDivElement, DeactivateUserAlertProps>(function DeactivateUserAlert(
  { user }: DeactivateUserAlertProps,
  ref: Ref<HTMLDivElement>
) {
  const { hasPermission } = usePermissions();
  const canDeactivate = hasPermission(UserPermissions.DEACTIVATE_USER);
  const queryClient = useQueryClient();
  const mutation = useChangeUser();

  const isActive = user.is_active;

  const toggleUserActive = () => {
    if (!canDeactivate) {
      return;
    }
    mutation.mutate(
      {
        userId: user.id,
        data: {
          is_active: !isActive,
        },
      },
      {
        onSuccess: () => {
          toast.success(isActive ? 'User deactivated.' : 'User reactivated.', {
            autoClose: 3000,
          });
          queryClient.invalidateQueries({ queryKey: ['userList'] });
        },
        onError: () => {
          toast.error(isActive ? 'Could not deactivate user.' : 'Could not reactivate user.', {
            autoClose: 3000,
          });
        },
      }
    );
  };

  return (
    <Alert
      trigger={<div ref={ref} className="hidden" />}
      title="Are you sure?"
      description={
        isActive
          ? 'This will prevent the user from logging in. They will remain visible in the user list as inactive.'
          : "This will restore the user's access and allow them to log in again."
      }
      actionLabel="Continue"
      onActionAccept={toggleUserActive}
    />
  );
});
